import { get, set, del, keys } from 'idb-keyval';

// Storage keys
const KEYS = {
  PRODUCTS: 'offline_products',
  MANUFACTURERS: 'offline_manufacturers',
  POS_LIST: 'offline_pos_list',
  DISTRIBUTORS: 'offline_distributors',
  SUBMISSION_QUEUE: 'offline_submission_queue',
  LAST_SYNC_PRODUCTS: 'last_sync_products',
  LAST_SYNC_MANUFACTURERS: 'last_sync_manufacturers',
  LAST_SYNC_POS: 'last_sync_pos',
  LAST_SYNC_DISTRIBUTORS: 'last_sync_distributors',
};

// Types
export interface QueuedSubmission {
  id: string;
  type: 'visit' | 'first_time_visit' | 'product_intro' | 'revisit';
  data: any;
  timestamp: number;
  retryCount: number;
}

// Products cache
export async function saveProducts(products: any[]): Promise<void> {
  try {
    await set(KEYS.PRODUCTS, products);
    await set(KEYS.LAST_SYNC_PRODUCTS, Date.now());
  } catch (error) {
    console.error('Failed to save products to cache:', error);
  }
}

export async function getProducts(): Promise<any[] | null> {
  try {
    const result = await get(KEYS.PRODUCTS);
    return result || null;
  } catch (error) {
    console.error('Failed to get products from cache:', error);
    return null;
  }
}

// Manufacturers cache
export async function saveManufacturers(manufacturers: any[]): Promise<void> {
  try {
    await set(KEYS.MANUFACTURERS, manufacturers);
    await set(KEYS.LAST_SYNC_MANUFACTURERS, Date.now());
  } catch (error) {
    console.error('Failed to save manufacturers to cache:', error);
  }
}

export async function getManufacturers(): Promise<any[] | null> {
  try {
    const result = await get(KEYS.MANUFACTURERS);
    return result || null;
  } catch (error) {
    console.error('Failed to get manufacturers from cache:', error);
    return null;
  }
}

// PoS cache
export async function savePoSList(posList: any[]): Promise<void> {
  try {
    await set(KEYS.POS_LIST, posList);
    await set(KEYS.LAST_SYNC_POS, Date.now());
  } catch (error) {
    console.error('Failed to save PoS list to cache:', error);
  }
}

export async function getPoSList(): Promise<any[] | null> {
  try {
    const result = await get(KEYS.POS_LIST);
    return result || null;
  } catch (error) {
    console.error('Failed to get PoS list from cache:', error);
    return null;
  }
}

// Distributors cache
export async function saveDistributors(distributors: any[]): Promise<void> {
  try {
    await set(KEYS.DISTRIBUTORS, distributors);
    await set(KEYS.LAST_SYNC_DISTRIBUTORS, Date.now());
  } catch (error) {
    console.error('Failed to save distributors to cache:', error);
  }
}

export async function getDistributors(): Promise<any[] | null> {
  try {
    const result = await get(KEYS.DISTRIBUTORS);
    return result || null;
  } catch (error) {
    console.error('Failed to get distributors from cache:', error);
    return null;
  }
}

// Sync timestamps
export async function getLastSyncTime(type: 'products' | 'pos' | 'distributors'): Promise<number | null> {
  try {
    const key = type === 'products' ? KEYS.LAST_SYNC_PRODUCTS : 
                type === 'pos' ? KEYS.LAST_SYNC_POS : KEYS.LAST_SYNC_DISTRIBUTORS;
    const result = await get(key);
    return result || null;
  } catch (error) {
    console.error(`Failed to get last sync time for ${type}:`, error);
    return null;
  }
}

// Submission queue
export async function addToSubmissionQueue(
  type: QueuedSubmission['type'],
  data: any
): Promise<void> {
  try {
    const queue = await getSubmissionQueue();
    const newSubmission: QueuedSubmission = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      data,
      timestamp: Date.now(),
      retryCount: 0,
    };
    queue.push(newSubmission);
    await set(KEYS.SUBMISSION_QUEUE, queue);
  } catch (error) {
    console.error('Failed to add to submission queue:', error);
    throw error;
  }
}

export async function getSubmissionQueue(): Promise<QueuedSubmission[]> {
  try {
    const queue = await get<QueuedSubmission[]>(KEYS.SUBMISSION_QUEUE);
    return queue || [];
  } catch (error) {
    console.error('Failed to get submission queue:', error);
    return [];
  }
}

export async function removeFromQueue(id: string): Promise<void> {
  try {
    const queue = await getSubmissionQueue();
    const updatedQueue = queue.filter((item) => item.id !== id);
    await set(KEYS.SUBMISSION_QUEUE, updatedQueue);
  } catch (error) {
    console.error('Failed to remove from queue:', error);
  }
}

export async function updateQueueItemRetryCount(id: string): Promise<void> {
  try {
    const queue = await getSubmissionQueue();
    const item = queue.find((item) => item.id === id);
    if (item) {
      item.retryCount += 1;
      await set(KEYS.SUBMISSION_QUEUE, queue);
    }
  } catch (error) {
    console.error('Failed to update retry count:', error);
  }
}

// Clear all cache
export async function clearAllCache(): Promise<void> {
  try {
    const allKeys = await keys();
    for (const key of allKeys) {
      if (typeof key === 'string' && key.startsWith('offline_')) {
        await del(key);
      }
    }
  } catch (error) {
    console.error('Failed to clear cache:', error);
  }
}

// Check if data is stale (older than 1 hour)
export async function isDataStale(type: 'products' | 'pos' | 'distributors'): Promise<boolean> {
  const lastSync = await getLastSyncTime(type);
  if (!lastSync) return true;
  
  const ONE_HOUR = 60 * 60 * 1000;
  return Date.now() - lastSync > ONE_HOUR;
}
